import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Patientlage = {
    id: number;
    code: string;
    displayName: string;
    ordning: number;
};

export type PatientlageDisplay = {
    value: Patientlage["id"];
    label: string;
};

type State = BaseState & {
    patientlagen: Map<Patientlage["id"], Patientlage>;
};

export const convertPatientlageData: (args: { patientlage?: Patientlage }) => PatientlageDisplay | null = ({
    patientlage,
}) => {
    if (!patientlage) {
        return null;
    }

    return {
        value: patientlage.id,
        label: patientlage.displayName,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        patientlagen: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "patientlagen";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        patientlagen: (state: State) => [...state.patientlagen.values()],
        patientlagenSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertPatientlageData({ patientlage: singleResult }));
            }

            return result;
        },
        patientlageById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.patientlagen.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addPatientlage: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                patientlagen = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                patientlagen: Patientlage[];
            }
        ) =>
            subsetSetter<State, Patientlage>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: patientlagen,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchPatientlagen: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Patientlage>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/oplagen/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addPatientlage", {
                    subsetName,
                    offset: resultOffset,
                    patientlagen: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertPatientlageData({ patientlage: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
